import { FC, useEffect, useState } from 'react';
import { FormBusinessRule } from 'src/shared/store/projects/types';
import { Button } from 'src/shared/ui';

type RuleMapEntry = {
  source: string;
  target: string;
};

type BusinessRulesBuilderMode = 'expression' | 'full' | 'consolidation';

type BusinessRulesBuilderProps = {
  rules: FormBusinessRule[];
  availableColumns?: string[];
  availableTables?: string[];
  onChange: (rules: FormBusinessRule[]) => void;
  mode?: BusinessRulesBuilderMode;
};

const ruleTypeOptions: Array<{ value: string; label: string }> = [
  { value: 'math', label: 'Математика' },
  { value: 'aggregate', label: 'Агрегация' },
  { value: 'transfer', label: 'Перенос' },
  { value: 'consolidation', label: 'Консолидация' },
  { value: 'python', label: 'Python' },
];

const aggregateFunctionOptions: Array<{ value: string; label: string }> = [
  { value: 'counta', label: 'Количество непустых (COUNTA)' },
  { value: 'product', label: 'Произведение (PRODUCT)' },
  { value: 'sum', label: 'Сумма (SUM)' },
  { value: 'count', label: 'Количество (COUNT)' },
  { value: 'avg', label: 'Среднее (AVG)' },
  { value: 'min', label: 'Минимум (MIN)' },
  { value: 'max', label: 'Максимум (MAX)' },
];

const consolidationOperationOptions: Array<{ value: string; label: string }> = [
  { value: 'sum', label: 'SUM (сумма)' },
  { value: 'set', label: 'Установить (SET)' },
  { value: 'max', label: 'Максимум (MAX)' },
  { value: 'min', label: 'Минимум (MIN)' },
];

const createRuleId = () => `rule_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const toStringValue = (value: unknown): string =>
  value === undefined || value === null ? '' : String(value);

const mapValueToEntries = (raw: unknown): RuleMapEntry[] => {
  if (Array.isArray(raw)) {
    return raw
      .map(item => {
        if (!isPlainObject(item)) {
          return null;
        }

        const source = toStringValue(item.source).trim();
        const target = toStringValue(item.target).trim();
        if (!source && !target) {
          return null;
        }

        return { source, target };
      })
      .filter(Boolean) as RuleMapEntry[];
  }

  if (!isPlainObject(raw)) {
    return [];
  }

  return Object.entries(raw)
    .map(([source, target]) => ({
      source: source.trim(),
      target: toStringValue(target).trim(),
    }))
    .filter(entry => entry.source || entry.target);
};

const entriesToMapValue = (entries: RuleMapEntry[]): Record<string, string> =>
  entries.reduce<Record<string, string>>((acc, entry) => {
    const source = entry.source.trim();
    const target = entry.target.trim();

    if (!source || !target) {
      return acc;
    }

    acc[source] = target;
    return acc;
  }, {});

const getCompletedRuleMapEntries = (entries: RuleMapEntry[]): RuleMapEntry[] =>
  mapValueToEntries(entriesToMapValue(entries));

const areRuleMapEntriesEqual = (left: RuleMapEntry[], right: RuleMapEntry[]): boolean =>
  left.length === right.length &&
  left.every(
    (entry, index) =>
      entry.source === right[index]?.source && entry.target === right[index]?.target,
  );

const isEmptyConfigValue = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === 'string') {
    return !value.trim();
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  if (isPlainObject(value)) {
    return Object.keys(value).length === 0;
  }
  return false;
};

const compactConfig = (config?: Record<string, unknown>): Record<string, unknown> | undefined => {
  if (!isPlainObject(config)) {
    return undefined;
  }

  const compacted = Object.entries(config).reduce<Record<string, unknown>>((acc, [key, value]) => {
    if (isEmptyConfigValue(value)) {
      return acc;
    }
    acc[key] = value;
    return acc;
  }, {});

  return Object.keys(compacted).length ? compacted : undefined;
};

const createDefaultRule = (type: string = 'math'): FormBusinessRule => ({
  id: createRuleId(),
  name: '',
  type,
  priority: 1,
  expression: '',
  targetColumn: '',
  enabled: true,
  config:
    type === 'aggregate'
      ? { function: 'sum' }
      : type === 'consolidation'
        ? { operation: 'sum' }
        : {},
});

const normalizeRule = (rule: FormBusinessRule, fallbackIndex: number): FormBusinessRule => ({
  id: rule.id || `rule_${fallbackIndex + 1}`,
  name: rule.name || '',
  type: (rule.type || 'math').toLowerCase(),
  priority:
    Number.isFinite(rule.priority) && Number(rule.priority) > 0
      ? Math.trunc(Number(rule.priority))
      : fallbackIndex + 1,
  expression: rule.expression || '',
  targetColumn: rule.targetColumn || '',
  enabled: rule.enabled !== false,
  config: { ...(rule.config || {}) },
});

type MappingEditorProps = {
  title: string;
  entries: RuleMapEntry[];
  onChange: (entries: RuleMapEntry[]) => void;
  sourcePlaceholder: string;
  targetPlaceholder: string;
  addLabel: string;
  sourceListId?: string;
  targetListId?: string;
};

const MappingEditor: FC<MappingEditorProps> = ({
  title,
  entries,
  onChange,
  sourcePlaceholder,
  targetPlaceholder,
  addLabel,
  sourceListId,
  targetListId,
}) => {
  const [draftEntries, setDraftEntries] = useState<RuleMapEntry[]>(() =>
    entries.length ? entries : [],
  );

  useEffect(() => {
    if (areRuleMapEntriesEqual(entries, getCompletedRuleMapEntries(draftEntries))) {
      return;
    }

    setDraftEntries(entries.length ? entries : []);
  }, [draftEntries, entries]);

  const updateEntry = (index: number, patch: Partial<RuleMapEntry>) => {
    const next = [...draftEntries];
    next[index] = { ...next[index], ...patch };
    setDraftEntries(next);
    onChange(next);
  };

  const removeEntry = (index: number) => {
    const next = draftEntries.filter((_, i) => i !== index);
    setDraftEntries(next);
    onChange(next);
  };

  const addEntry = () => {
    const next = [...draftEntries, { source: '', target: '' }];
    setDraftEntries(next);
    onChange(next);
  };

  return (
    <div className="rounded border border-blue-400 p-2">
      <div className="mb-2 text-xs opacity-70 dark:text-white">{title}</div>
      {!!draftEntries.length && (
        <div className="space-y-2">
          {draftEntries.map((entry, index) => (
            <div key={`${title}-${index}`} className="grid grid-cols-12 gap-2">
              <input
                value={entry.source}
                onChange={event => updateEntry(index, { source: event.target.value })}
                placeholder={sourcePlaceholder}
                list={sourceListId}
                className="col-span-5 rounded border border-violet-400 bg-white p-2 text-xs dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
              />
              <input
                value={entry.target}
                onChange={event => updateEntry(index, { target: event.target.value })}
                placeholder={targetPlaceholder}
                list={targetListId}
                className="col-span-5 rounded border border-violet-400 bg-white p-2 text-xs dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
              />
              <Button
                type="button"
                variant="greyLight"
                size="small"
                classNames="col-span-2"
                onClick={() => removeEntry(index)}
              >
                Удалить
              </Button>
            </div>
          ))}
        </div>
      )}
      <Button type="button" size="small" variant="violet" classNames="mt-2" onClick={addEntry}>
        {addLabel}
      </Button>
    </div>
  );
};

const getModeMeta = (mode: BusinessRulesBuilderMode) => {
  if (mode === 'expression') {
    return {
      sectionTitle: 'Бизнес-правила',
      addLabel: '+ Добавить правило',
      description:
        'Используйте выражения вида [Цена] * [Количество]. Нематематические правила сохраняются без изменений и будут вынесены в отдельные сценарии настройки.',
      emptyState:
        'Правила выражений не настроены. Добавьте правило для расчета значения целевой колонки.',
    };
  }

  if (mode === 'consolidation') {
    return {
      sectionTitle: 'Консолидация',
      addLabel: '+ Добавить источник',
      description:
        'Настройте по одному источнику в каждом блоке. Агрегация и сопоставление применяются к текущей таблице.',
      emptyState:
        'Правила консолидации не настроены. Добавьте таблицу-источник для сопоставления ключей и значений.',
    };
  }

  return {
    sectionTitle: 'Бизнес-правила',
    addLabel: 'Добавить правило',
    description:
      'Правила выполняются по приоритету сверху вниз. Если включена опция формы `Останавливать правила при первой ошибке`, включайте `Продолжать при ошибке правила` для исключений.',
    emptyState:
      'Правила не настроены. Добавьте правило для вычислений, агрегации, переноса или консолидации.',
  };
};

export const BusinessRulesBuilder: FC<BusinessRulesBuilderProps> = ({
  rules,
  availableColumns = [],
  availableTables = [],
  onChange,
  mode = 'expression',
}) => {
  const [draggedRuleIndex, setDraggedRuleIndex] = useState<number | null>(null);
  const isExpressionMode = mode === 'expression';
  const isConsolidationMode = mode === 'consolidation';
  const singleRuleType = isExpressionMode
    ? 'math'
    : isConsolidationMode
      ? 'consolidation'
      : undefined;
  const modeMeta = getModeMeta(mode);

  const columnSuggestions = Array.from(
    new Set(availableColumns.map(column => column.trim()).filter(Boolean)),
  );
  const tableSuggestions = Array.from(
    new Set(availableTables.map(table => table.trim()).filter(Boolean)),
  );
  const rowValueSuggestions = columnSuggestions.map(column => `$row.${column}`);

  const normalizedRules = rules.map((rule, index) => normalizeRule(rule, index));
  const safeRules = singleRuleType
    ? normalizedRules.filter(rule =>
        singleRuleType === 'math'
          ? !rule.type || rule.type === singleRuleType
          : rule.type === singleRuleType,
      )
    : normalizedRules;
  const preservedRules = singleRuleType
    ? normalizedRules.filter(rule =>
        singleRuleType === 'math'
          ? rule.type && rule.type !== singleRuleType
          : rule.type !== singleRuleType,
      )
    : [];

  const updateRules = (nextRules: FormBusinessRule[]) => {
    const normalizedVisibleRules = nextRules.map((rule, index) => ({
      ...rule,
      ...(singleRuleType ? { type: singleRuleType } : {}),
      priority: index + 1,
      config: compactConfig(rule.config),
    }));

    if (!singleRuleType) {
      onChange(normalizedVisibleRules);
      return;
    }

    onChange([
      ...normalizedVisibleRules,
      ...preservedRules.map((rule, index) => ({
        ...rule,
        priority: normalizedVisibleRules.length + index + 1,
        config: compactConfig(rule.config),
      })),
    ]);
  };

  const updateRule = (index: number, updater: (rule: FormBusinessRule) => FormBusinessRule) => {
    const nextRules = [...safeRules];
    nextRules[index] = normalizeRule(updater(nextRules[index]), index);
    updateRules(nextRules);
  };

  const updateRuleConfigValue = (index: number, key: string, value: unknown) => {
    updateRule(index, rule => {
      const nextConfig: Record<string, unknown> = { ...(rule.config || {}) };

      if (isEmptyConfigValue(value)) {
        delete nextConfig[key];
      } else {
        nextConfig[key] = value;
      }

      return { ...rule, config: nextConfig };
    });
  };

  const updateRuleMapConfig = (index: number, key: string, entries: RuleMapEntry[]) => {
    updateRuleConfigValue(index, key, entriesToMapValue(entries));
  };

  const addRule = () => {
    updateRules([...safeRules, createDefaultRule(singleRuleType || 'math')]);
  };

  const removeRule = (index: number) => {
    updateRules(safeRules.filter((_, ruleIndex) => ruleIndex !== index));
  };

  const reorderRules = (sourceIndex: number, targetIndex: number) => {
    if (sourceIndex === targetIndex) {
      return;
    }

    const nextRules = [...safeRules];
    const [moved] = nextRules.splice(sourceIndex, 1);
    if (!moved) {
      return;
    }

    nextRules.splice(targetIndex, 0, moved);
    updateRules(nextRules);
  };

  const changeType = (index: number, type: string) => {
    updateRule(index, rule => {
      const normalizedType = type.toLowerCase();
      const nextConfig: Record<string, unknown> = { ...(rule.config || {}) };

      if (normalizedType === 'aggregate' && !nextConfig.function) {
        nextConfig.function = 'sum';
      }
      if (normalizedType === 'consolidation' && !nextConfig.operation) {
        nextConfig.operation = 'sum';
      }

      return {
        ...rule,
        type: normalizedType,
        config: nextConfig,
      };
    });
  };

  const getNameInputClassNames = () => {
    if (isExpressionMode) {
      return 'col-span-5';
    }
    if (isConsolidationMode) {
      return 'col-span-10';
    }
    return 'col-span-4';
  };

  return (
    <div className="mt-5 rounded border border-blue-400 p-3">
      <div className="mb-3 flex items-center justify-between">
        <div className="text-xs opacity-70 dark:text-white">{modeMeta.sectionTitle}</div>
        <Button type="button" size="small" variant="green" onClick={addRule}>
          {modeMeta.addLabel}
        </Button>
      </div>

      <div className="mb-3 text-xs text-grey-300">{modeMeta.description}</div>

      {!!columnSuggestions.length && (
        <datalist id="business-rule-columns">
          {columnSuggestions.map(column => (
            <option key={`column-${column}`} value={column} />
          ))}
        </datalist>
      )}

      {!!tableSuggestions.length && (
        <datalist id="business-rule-tables">
          {tableSuggestions.map(table => (
            <option key={`table-${table}`} value={table} />
          ))}
        </datalist>
      )}

      {!!rowValueSuggestions.length && (
        <datalist id="business-rule-row-values">
          {rowValueSuggestions.map(value => (
            <option key={`row-value-${value}`} value={value} />
          ))}
        </datalist>
      )}

      {!safeRules.length && <div className="text-xs text-grey-300">{modeMeta.emptyState}</div>}

      <div className="space-y-4">
        {safeRules.map((rule, index) => {
          const config: Record<string, unknown> = { ...(rule.config || {}) };
          const continueOnError = Boolean(config.continueOnError);
          const aggregateFunction = toStringValue(config.function || 'sum').toLowerCase();
          const aggregateWhere = mapValueToEntries(config.where);
          const transferColumnMap = mapValueToEntries(config.columnMap);
          const transferMatchBy = mapValueToEntries(config.matchBy);
          const consolidationKeyMap = mapValueToEntries(config.keyMap);
          const consolidationValueMap = mapValueToEntries(config.valueMap);
          const consolidationOperation = toStringValue(config.operation || 'sum').toLowerCase();

          return (
            <div
              key={rule.id || `rule-${index}`}
              className={`rounded border border-violet-400 p-3 ${
                draggedRuleIndex === index ? 'opacity-60' : ''
              }`}
              draggable
              onDragStart={() => setDraggedRuleIndex(index)}
              onDragOver={event => event.preventDefault()}
              onDrop={() => {
                if (draggedRuleIndex === null) {
                  return;
                }
                reorderRules(draggedRuleIndex, index);
                setDraggedRuleIndex(null);
              }}
              onDragEnd={() => setDraggedRuleIndex(null)}
            >
              <div className="mb-3 grid grid-cols-12 gap-2">
                <div
                  className="col-span-1 flex items-center justify-center text-xs text-grey-300"
                  title="Перетащите для изменения порядка"
                >
                  #{index + 1}
                </div>

                <input
                  value={rule.name || ''}
                  onChange={event =>
                    updateRule(index, current => ({ ...current, name: event.target.value }))
                  }
                  placeholder="Название правила"
                  className={`${getNameInputClassNames()} rounded border border-violet-400 bg-white p-2 text-xs dark:border-violet-900 dark:bg-violet-1200 dark:text-white`}
                />

                {!singleRuleType && (
                  <select
                    value={rule.type}
                    onChange={event => changeType(index, event.target.value)}
                    className="col-span-3 rounded border border-violet-400 bg-white p-2 text-xs dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
                  >
                    {ruleTypeOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                )}

                {!isConsolidationMode && (
                  <input
                    value={rule.targetColumn || ''}
                    onChange={event =>
                      updateRule(index, current => ({
                        ...current,
                        targetColumn: event.target.value,
                      }))
                    }
                    placeholder="Целевая колонка"
                    list="business-rule-columns"
                    className={`${
                      isExpressionMode ? 'col-span-5' : 'col-span-3'
                    } rounded border border-violet-400 bg-white p-2 text-xs dark:border-violet-900 dark:bg-violet-1200 dark:text-white`}
                  />
                )}

                <Button
                  type="button"
                  variant="greyLight"
                  size="small"
                  classNames="col-span-1"
                  onClick={() => removeRule(index)}
                >
                  X
                </Button>
              </div>

              <div className="mb-3 flex items-center gap-6 text-xs dark:text-white">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={rule.enabled !== false}
                    onChange={event =>
                      updateRule(index, current => ({ ...current, enabled: event.target.checked }))
                    }
                  />
                  Включено
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={continueOnError}
                    onChange={event =>
                      updateRuleConfigValue(index, 'continueOnError', event.target.checked)
                    }
                  />
                  {isExpressionMode ? 'Обнулять ячейки с ошибкой' : 'Продолжать при ошибке правила'}
                </label>
              </div>

              {rule.type === 'math' && (
                <div className="grid grid-cols-1 gap-2">
                  <input
                    value={rule.expression || toStringValue(config.expression)}
                    onChange={event =>
                      updateRule(index, current => ({
                        ...current,
                        expression: event.target.value,
                        config: { ...(current.config || {}), expression: event.target.value },
                      }))
                    }
                    placeholder={
                      isExpressionMode
                        ? 'Выражение, например [Цена] * [Количество]'
                        : 'Выражение, например цена * количество'
                    }
                    className="rounded border border-violet-400 bg-white p-2 text-xs dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
                  />
                </div>
              )}

              {rule.type === 'aggregate' && (
                <div className="space-y-2">
                  <div className="grid grid-cols-12 gap-2">
                    <select
                      value={aggregateFunction}
                      onChange={event =>
                        updateRuleConfigValue(index, 'function', event.target.value)
                      }
                      className="col-span-3 rounded border border-violet-400 bg-white p-2 text-xs dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
                    >
                      {aggregateFunctionOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <input
                      value={toStringValue(config.sourceColumn)}
                      onChange={event =>
                        updateRuleConfigValue(index, 'sourceColumn', event.target.value)
                      }
                      placeholder="Исходная колонка"
                      list="business-rule-columns"
                      className="col-span-5 rounded border border-violet-400 bg-white p-2 text-xs dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
                    />
                    <label className="col-span-4 flex items-center gap-2 text-xs dark:text-white">
                      <input
                        type="checkbox"
                        checked={Boolean(config.excludeCurrentRow)}
                        onChange={event =>
                          updateRuleConfigValue(index, 'excludeCurrentRow', event.target.checked)
                        }
                      />
                      Исключить текущую строку
                    </label>
                  </div>
                  <MappingEditor
                    title="Условия отбора (колонка => значение, можно $row.column)"
                    entries={aggregateWhere}
                    onChange={entries => updateRuleMapConfig(index, 'where', entries)}
                    sourcePlaceholder="Колонка"
                    targetPlaceholder="Значение или $row.column"
                    addLabel="Добавить условие"
                    sourceListId="business-rule-columns"
                    targetListId="business-rule-row-values"
                  />
                </div>
              )}

              {rule.type === 'transfer' && (
                <div className="space-y-2">
                  <input
                    value={toStringValue(config.targetTable)}
                    onChange={event =>
                      updateRuleConfigValue(index, 'targetTable', event.target.value)
                    }
                    placeholder="Целевая таблица"
                    list="business-rule-tables"
                    className="w-full rounded border border-violet-400 bg-white p-2 text-xs dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
                  />
                  <MappingEditor
                    title="Сопоставление колонок (источник => цель)"
                    entries={transferColumnMap}
                    onChange={entries => updateRuleMapConfig(index, 'columnMap', entries)}
                    sourcePlaceholder="Исходная колонка"
                    targetPlaceholder="Целевая колонка"
                    addLabel="Добавить сопоставление"
                    sourceListId="business-rule-columns"
                    targetListId="business-rule-columns"
                  />
                  <MappingEditor
                    title="Связка по ключам (источник => цель)"
                    entries={transferMatchBy}
                    onChange={entries => updateRuleMapConfig(index, 'matchBy', entries)}
                    sourcePlaceholder="Исходная колонка"
                    targetPlaceholder="Целевая ключевая колонка"
                    addLabel="Добавить ключ"
                    sourceListId="business-rule-columns"
                    targetListId="business-rule-columns"
                  />
                </div>
              )}

              {rule.type === 'consolidation' && (
                <div className="space-y-2">
                  <div className="grid grid-cols-12 gap-2">
                    <input
                      value={toStringValue(config.targetTable)}
                      onChange={event =>
                        updateRuleConfigValue(index, 'targetTable', event.target.value)
                      }
                      placeholder="Таблица-источник"
                      list="business-rule-tables"
                      className="col-span-8 rounded border border-violet-400 bg-white p-2 text-xs dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
                    />
                    <select
                      value={consolidationOperation}
                      onChange={event =>
                        updateRuleConfigValue(index, 'operation', event.target.value)
                      }
                      className="col-span-4 rounded border border-violet-400 bg-white p-2 text-xs dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
                    >
                      {consolidationOperationOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <MappingEditor
                    title="Сопоставление ключей (источник => текущая таблица)"
                    entries={consolidationKeyMap}
                    onChange={entries => updateRuleMapConfig(index, 'keyMap', entries)}
                    sourcePlaceholder="Исходный ключ"
                    targetPlaceholder="Ключевая колонка текущей таблицы"
                    addLabel="Добавить сопоставление ключей"
                    sourceListId="business-rule-columns"
                    targetListId="business-rule-columns"
                  />

                  <MappingEditor
                    title="Сопоставление значений (источник => текущая таблица)"
                    entries={consolidationValueMap}
                    onChange={entries => updateRuleMapConfig(index, 'valueMap', entries)}
                    sourcePlaceholder="Исходное значение"
                    targetPlaceholder="Колонка текущей таблицы"
                    addLabel="Добавить сопоставление значений"
                    sourceListId="business-rule-columns"
                    targetListId="business-rule-columns"
                  />
                </div>
              )}

              {rule.type === 'python' && (
                <div className="space-y-2">
                  <textarea
                    value={rule.expression || toStringValue(config.expression)}
                    onChange={event =>
                      updateRule(index, current => ({
                        ...current,
                        expression: event.target.value,
                        config: { ...(current.config || {}), expression: event.target.value },
                      }))
                    }
                    placeholder="Например: round((row['price'] or 0) * 1.2, 2)"
                    className="min-h-32 w-full rounded border border-violet-400 bg-white p-2 text-xs dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
                  />
                  <div className="text-xs text-grey-300">
                    Поддерживается только безопасное Python-выражение. Доступны `row`, `row_id`,
                    `table_name`, функции `abs`, `min`, `max`, `round`, `sum`, `len`, `int`,
                    `float`, `str`, `bool` и модули `math`, `statistics`, `decimal`, `Decimal`.
                    Результат должен быть скалярным значением и будет записан в целевую колонку.
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
